'use client'
import cn from 'classnames'
import { cubicBezier } from 'motion'
import { motion } from 'motion/react'
import { type FC, useEffect, useState } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { Content } from '@/content-types'
import { asText, isFilled } from '@/lib/content-fields'
import { type Currency, detectCurrency, readStoredCurrency, storeCurrency } from '@/lib/currency'
import { ContentLink, RichText, type SliceComponentProps } from '@/lib/rich-text'
import { getBackgroundColor } from '@/utils/helpers'
import { linkResolver } from '@/utils/linkResolver'

/**
 * Props for `PriceCard`.
 */
export type PriceCardProps = SliceComponentProps<Content.PriceCardSlice>

/**
 * Component for "PriceCard" Slices.
 */
const PriceCard: FC<PriceCardProps> = ({ slice }) => {
  /**
   * EUR on the server and in the prerendered HTML, so hydration matches. The
   * visitor's own currency is resolved after mount: a choice they made before
   * wins, otherwise their timezone decides, using the same eight US zones the
   * app uses when it opens the billing account.
   */
  const [currency, setCurrency] = useState<Currency>('EUR')

  useEffect(() => {
    setCurrency(readStoredCurrency() ?? detectCurrency())
  }, [])

  const chooseCurrency = (next: Currency) => {
    setCurrency(next)
    storeCurrency(next)
  }

  // Only offer the switch when there is something to switch to.
  const hasUsd = slice.primary.pricecard.some((card) => isFilled.richText(card.price_usd))

  const hasTitleOrText = isFilled.richText(slice.primary.title) || isFilled.richText(slice.primary.text)

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={getBackgroundColor(slice.primary.background)}
    >
      {hasUsd && (
        <div className="container mb-8 flex justify-center">
          <fieldset className="inline-flex rounded-full bg-gray-100 p-1 ring-1 ring-black/5">
            <legend className="sr-only">Currency</legend>
            {(['EUR', 'USD'] as const).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => chooseCurrency(code)}
                aria-pressed={currency === code}
                className={cn(
                  'rounded-full px-4 py-1.5 text-sm font-semibold transition duration-200',
                  currency === code ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-900'
                )}
              >
                {code === 'EUR' ? '€ EUR' : '$ USD'}
              </button>
            ))}
          </fieldset>
        </div>
      )}

      <div
        className={cn(
          'container',
          hasTitleOrText
            ? 'flex flex-col items-start gap-8 md:grid md:grid-cols-3 md:gap-9 md:space-y-0'
            : slice.primary.pricecard.length === 2
              ? 'flex flex-col items-center md:grid md:grid-cols-2 md:flex-row md:gap-8'
              : slice.primary.pricecard.length === 3
                ? 'flex flex-col items-center md:grid md:grid-cols-3 md:flex-row md:gap-8'
                : 'flex flex-col items-center md:grid md:grid-cols-2'
        )}
      >
        {/* Only render title section if title or text exists */}
        {hasTitleOrText && (
          <motion.div
            className="col-span-3 overflow-hidden md:col-span-1 md:overflow-visible"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
              duration: 0.6,
            }}
          >
            <RichText
              field={slice.primary.title}
              components={{
                heading2: ({ children }) => <h2 className="section-title">{children}</h2>,
                heading3: ({ children }) => <h3 className="section-title">{children}</h3>,
              }}
            />
            {isFilled.richText(slice.primary.title) && (
              <hr className="border-primary mt-8 mb-6 w-[68px] border-b-4" />
            )}

            {isFilled.richText(slice.primary.text) && (
              <RichText
                field={slice.primary.text}
                components={{
                  paragraph: ({ children }) => <p className="text-[22px] font-light">{children}</p>,
                }}
              />
            )}

            {isFilled.link(slice.primary.button) && (
              <div className="mt-8 mb-8">
                <ContentLink
                  field={slice.primary.button}
                  linkResolver={linkResolver}
                  className="group hover:text-primary flex items-center justify-start gap-2 font-semibold text-nowrap transition duration-200 ease-in-out md:text-xs xl:text-lg"
                >
                  <IconComponent
                    icon="ChevronRight"
                    size="xsmall"
                    className="group-hover:text-primary text-primary"
                  />

                  {slice.primary.button.text}
                </ContentLink>
              </div>
            )}
          </motion.div>
        )}

        {slice.primary.pricecard.map((priceCard) => (
          <motion.div
            key={asText(priceCard.title)}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
              duration: 0.6,
              delay: 0.1,
            }}
            className={cn(
              'relative h-full w-full overflow-hidden rounded-3xl px-7 py-8 transition duration-300 hover:-translate-y-1 lg:px-10 lg:py-10',
              priceCard.background === 'White' &&
                'bg-white ring-1 ring-black/10 shadow-[0_1px_2px_rgba(45,3,59,0.06)] hover:shadow-[0_16px_40px_-20px_rgba(129,12,168,0.45)]',
              priceCard.background === 'Gray' && 'bg-gray-100 ring-1 ring-black/5',
              priceCard.background === 'Gradiant' &&
                'bg-gradient-to-b from-[rgb(129,12,168)] from-[0%] to-[rgb(45,3,59)] to-[100%] text-white shadow-[0_24px_60px_-24px_rgba(129,12,168,0.7)] md:-translate-y-3 md:hover:-translate-y-5 md:py-11'
            )}
          >
            {priceCard.background === 'Gradiant' && (
              <span className="absolute top-5 right-5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold tracking-wide text-white uppercase ring-1 ring-white/30">
                Most popular
              </span>
            )}
            <RichText
              field={priceCard.title}
              components={{
                heading4: ({ children }) => (
                  <h4
                    className={cn(
                      'text-[28px] leading-9 font-semibold tracking-tight',
                      priceCard.background === 'White' && 'text-primary-dark',
                      priceCard.background === 'Gray' && 'text-primary-dark',
                      priceCard.background === 'Gradiant' && 'text-white'
                    )}
                  >
                    {children}
                  </h4>
                ),
              }}
            />

            <div className="mt-3 flex flex-row items-center gap-x-4 py-3">
              <RichText
                field={
                  currency === 'USD' && isFilled.richText(priceCard.price_usd)
                    ? priceCard.price_usd
                    : priceCard.price
                }
                components={{
                  paragraph: ({ children }) => (
                    <p
                      className={cn(
                        'text-6xl font-bold tracking-tight break-words md:text-[64px]',
                        priceCard.background === 'Gradiant' && 'text-white'
                      )}
                    >
                      {children}
                    </p>
                  ),
                }}
              />
              <div className="flex flex-col">
                <RichText
                  field={
                    currency === 'USD' && isFilled.richText(priceCard.pricetext_usd)
                      ? priceCard.pricetext_usd
                      : priceCard.pricetext
                  }
                  components={{
                    paragraph: ({ children }) => <p className="text-[15px]/5 opacity-80">{children}</p>,
                  }}
                />
              </div>
            </div>

            {priceCard.tiers && priceCard.tiers.length > 0 && (
              <div
                className={cn(
                  'mt-1 mb-5 rounded-xl px-4 py-3',
                  priceCard.background === 'Gradiant' ? 'bg-white/10' : 'bg-black/[0.03]'
                )}
              >
                <p
                  className={cn(
                    'mb-2 text-[13px] font-semibold',
                    priceCard.background === 'Gradiant' ? 'text-white/90' : 'text-gray-700'
                  )}
                >
                  Volume pricing, applied automatically
                </p>
                <dl className="space-y-1">
                  {priceCard.tiers.map((tier) => (
                    <div key={tier.range} className="flex justify-between text-[13px]">
                      <dt
                        className={cn(priceCard.background === 'Gradiant' ? 'text-white/70' : 'opacity-70')}
                      >
                        {tier.range} screens
                      </dt>
                      <dd
                        className={cn(
                          'font-semibold tabular-nums',
                          priceCard.background === 'Gradiant' ? 'text-white' : ''
                        )}
                      >
                        {currency === 'USD' ? tier.price_usd : tier.price}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <RichText
              field={priceCard.text}
              components={{
                paragraph: ({ children }) => <p className="text-base">{children}</p>,
              }}
            />

            {priceCard.button && (
              <div className="mt-8 mb-6">
                <ContentLink
                  linkResolver={linkResolver}
                  field={priceCard.button[0]}
                  className={cn(
                    'button w-full !normal-case tracking-normal',
                    priceCard.background === 'White' &&
                      'border-primary text-primary hover:text-white bg-white',
                    priceCard.background === 'Gray' &&
                      'border-primary text-primary hover:text-white bg-white',
                    priceCard.background === 'Gradiant' &&
                      'text-primary border-white bg-white hover:border-white'
                  )}
                >
                  {priceCard.button[0]?.text}
                </ContentLink>
              </div>
            )}

            {/* Replace 'features' with the correct field name or remove this block if it doesn't exist */}
            <RichText
              field={priceCard.features}
              components={{
                paragraph: ({ children }) => <p className="text-[17px] font-semibold">{children}</p>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                list: ({ children }) => <ul className="mt-4 space-y-3">{children}</ul>,
                listItem: ({ children }) => (
                  <li className="flex items-start gap-2.5 text-[15px] leading-6">
                    <IconComponent
                      icon="CheckMark"
                      size="xsmall"
                      className="mt-1 !size-4 shrink-0 opacity-90"
                    />
                    <span>{children}</span>
                  </li>
                ),
              }}
            />
          </motion.div>
        ))}
      </div>
    </section>
  )
}

export default PriceCard
